import { useCallback } from 'react';
import { useApolloClient } from '@frameio/graphql-client';
import type { SendInviteEmailInput } from '@frameio/graphql-client/generated';
import { useSendInviteEmailMutation } from '@frameio/graphql-client/generated';
import { useToastTryCatchHandler } from '@frameio/utils/hooks/useToastTryCatchHandler';
import { readMembershipFragment } from '../utils';
import TEXT from './generated/TEXT/useSendInviteEmail';

TEXT.DEFINE({
  /** Error toast message when sending invitation fails */
  error: (name: string) =>
    `There was an issue sending a new invitation to ${name}`,

  /** Success toast message when invitation is sent */
  success: (name: string) => `${name} was invited again`,
});

export const useSendInviteEmail = () => {
  const tryCatchOnInvite = useToastTryCatchHandler();
  const [sendInviteEmailMutation] = useSendInviteEmailMutation();
  const client = useApolloClient();

  const sendInviteEmail = useCallback(
    async ({ accountUserId, membershipId }: SendInviteEmailInput) => {
      const invite = sendInviteEmailMutation({
        variables: { accountUserId, membershipId },
      });

      const { member, accountUser } = readMembershipFragment(
        client,
        accountUserId || membershipId
      );
      const displayName =
        member?.accountUser.displayName || accountUser?.displayName;

      tryCatchOnInvite({
        attempt: () => invite,
        successMessage: TEXT.success(displayName || '') as string,
        errorMessage: TEXT.error(displayName || '') as string,
      });
    },
    [client, sendInviteEmailMutation, tryCatchOnInvite]
  );

  return sendInviteEmail;
};
